import { useState, useEffect, useCallback, useRef } from "react";
import { GET, POST, PATCH } from "./api";
import { useTheme, ThemeProvider } from "./theme";
import { LangProvider, useLang } from "./i18n";
import LoginPage from "./LoginPage";
import AdminOverview from "./AdminOverview";
import AdminUsers from "./AdminUsers";
import AdminVMs from "./AdminVMs";
import AdminGPU from "./AdminGPU";
import AdminPorts from "./AdminPorts";
import AdminRequests from "./AdminRequests";
import AdminMetrics from "./AdminMetrics";
import UserMyVMs from "./UserMyVMs";
import UserSSH from "./UserSSH";
import UserServices from "./UserServices";
import UserRequests from "./UserRequests";
import GpuStatus from "./GpuStatus";
import UserGuide from "./UserGuide";

export default function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <AppInner />
      </LangProvider>
    </ThemeProvider>
  );
}

function AppInner() {
  const { lang, setLang, t } = useLang();
  const { C, mode, toggle: toggleTheme } = useTheme();
  const [me, setMe] = useState(() => {
    const saved = localStorage.getItem("gpu_fabric_user");
    return saved ? JSON.parse(saved) : null;
  });
  const [tab, setTab] = useState(null);

  const [servers, setServers] = useState([]);
  const [vms, setVms] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [sshKeys, setSshKeys] = useState([]);
  const [config, setConfig] = useState(null);
  const [metricsSummary, setMetricsSummary] = useState({});
  const [collabVms, setCollabVms] = useState([]);
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);

  const isAdmin = me?.role === "admin";

  useEffect(() => {
    if (me && !tab) setTab(isAdmin ? "overview" : "myvms");
  }, [me, tab, isAdmin]);

  // 정적 데이터(config·images)는 한 번만 받으면 되지만, 마운트 시 일시 실패하면
  // 세션 내내 null 로 고착돼 getServerIp 가 "?" 를 반환(→ SSH 명령 깨짐)하는 문제가 있었다.
  // 받을 때까지 폴링 틱마다 재시도하고, 성공하면 ref 플래그로 더 이상 호출하지 않는다.
  const staticLoadedRef = useRef({ config: false, images: false });
  const loadStatic = useCallback(() => {
    if (!staticLoadedRef.current.config)
      GET("/api/config").then(d => { setConfig(d); staticLoadedRef.current.config = true; }).catch(() => {});
    if (!staticLoadedRef.current.images)
      GET("/api/images").then(d => { setImages(d); staticLoadedRef.current.images = true; }).catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    if (!me) return;
    loadStatic();   // 정적 데이터 미수신분 재시도 (일시 실패 영구고착 방지)
    try {
      const [srvData, vmData, usrData, reqData, mData] = await Promise.all([
        GET("/api/servers"), GET("/api/vms"), GET("/api/users"),
        GET("/api/requests"), GET("/api/metrics-summary"),
      ]);
      setServers(srvData);
      setVms(vmData);
      setUsers(usrData);
      setRequests(reqData);
      setMetricsSummary(mData);

      if (isAdmin) {
        const svcData = await GET("/api/services");
        setServices(svcData);
      } else {
        // N+1 제거: VM별 /api/vms/{id}/services 반복 대신 1쿼리
        const [svcData, keyData, collabs] = await Promise.all([
          GET("/api/my-services"),
          GET(`/api/users/${me.id}/ssh-keys`),
          GET("/api/my-collabs"),
        ]);
        setServices(svcData);
        setSshKeys(keyData);
        setCollabVms(collabs);
      }
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [me, isAdmin, loadStatic]);

  // 폴링: 20초 주기 + 탭이 백그라운드면 건너뜀(다시 보이면 즉시 갱신).
  useEffect(() => {
    if (!me) return;
    fetchAll();
    const id = setInterval(() => { if (!document.hidden) fetchAll(); }, 20000);
    const onVisible = () => { if (!document.hidden) fetchAll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchAll, me]);

  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", currentPw: "", newPw: "" });

  const openProfile = () => {
    setProfileForm({ name: me?.name || "", currentPw: "", newPw: "" });
    setShowProfile(true);
  };

  const saveProfile = async () => {
    const body = {};
    if (profileForm.name && profileForm.name !== me.name) body.name = profileForm.name;
    if (profileForm.newPw) {
      body.password = profileForm.newPw;
      body.current_password = profileForm.currentPw;
    }
    if (!Object.keys(body).length) { setShowProfile(false); return; }
    try {
      await PATCH("/api/auth/profile", body);
      if (body.name) {
        const updated = { ...me, name: body.name };
        setMe(updated);
        localStorage.setItem("gpu_fabric_user", JSON.stringify(updated));
      }
      setShowProfile(false);
      alert(t("profile_saved") || "저장되었습니다");
    } catch (e) {
      alert(e.message || "저장 실패");
    }
  };

  const doLogout = async () => {
    try { await POST("/api/auth/logout", {}); } catch(e) {}
    localStorage.removeItem("gpu_fabric_token");
    localStorage.removeItem("gpu_fabric_user");
    setMe(null); setTab(null);
  };

  if (!me) return <LoginPage onLogin={u => { setMe(u); setTab(u.role==="admin"?"overview":"myvms"); if(u.lang) setLang(u.lang); }}/>;

  const allGpus = servers.flatMap(s => s.gpus || []);
  const allVmsList = servers.flatMap(s => s.vms || []);
  const pendingCount = requests.filter(r => r.status === "pending").length;

  const getServerIp = (sid) => config?.servers?.[sid]?.host || config?.servers?.[sid]?.ip || "?";

  const adminTabs = [
    { id:"overview", label:t("overview") },
    { id:"users", label:t("tab_users") },
    { id:"vms", label:t("tab_vms") },
    { id:"gpustatus", label:t("tab_gpu_status") },
    { id:"metrics", label:t("tab_metrics") },
    { id:"ports", label:t("tab_ports") },
    { id:"requests", label:t("tab_pending"), badge:pendingCount },
  ];
  const userTabs = [
    { id:"myvms", label:t("tab_my_vms") },
    { id:"gpustatus", label:t("tab_gpu_status") },
    { id:"ssh", label:t("tab_ssh") },
    { id:"services", label:t("tab_services") },
    { id:"requests", label:t("tab_requests") },
    { id:"guide", label:lang==="ko"?"안내":"Guide" },
  ];
  const tabs = isAdmin ? adminTabs : userTabs;

  const pageProps = {
    me, servers, vms, users, services, requests, sshKeys, config, collabVms, images,
    allGpus, allVmsList, metricsSummary, getServerIp, fetchAll, pendingCount,
  };

  return (
    <div key={mode} style={{ minHeight:"100vh", background:C.bg, color:C.text,
      fontFamily:"monospace", fontSize:12 }}>
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:.3}}
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;700&display=swap');
        *{scrollbar-width:thin;scrollbar-color:${C.border2} ${C.bg}}
        ::-webkit-scrollbar{width:6px}
        ::-webkit-scrollbar-track{background:${C.bg}}
        ::-webkit-scrollbar-thumb{background:${C.border2};border-radius:9px}`}</style>

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
        padding:"12px 24px", borderBottom:`1px solid ${C.border}`, background:C.panel }}>
        <div style={{ display:"flex", alignItems:"center", gap:18 }}>
          <span style={{ fontFamily:"'Rajdhani',sans-serif", fontWeight:700,
            fontSize:16, color:C.text, letterSpacing:2 }}>NEOALI GPUS</span>
          <div style={{ display:"flex", gap:2 }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                background:tab===t.id?C.border2+"88":"transparent",
                color:tab===t.id?C.text:C.dim2,
                border:`1px solid ${tab===t.id?C.border2:"transparent"}`,
                borderRadius:6, padding:"5px 13px", fontSize:10,
                fontFamily:"monospace", fontWeight:600, cursor:"pointer",
                display:"flex", alignItems:"center", gap:6,
              }}>
                {t.label}
                {t.badge > 0 && (
                  <span style={{ background:C.red, color:"#fff", fontSize:9,
                    padding:"1px 6px", borderRadius:20, fontWeight:700 }}>
                    {t.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          {error && <span style={{ color:C.red, fontSize:10 }}>{error}</span>}
          <button onClick={toggleTheme} style={{
            background:C.border2, color:C.text, border:`1px solid ${C.border2}`,
            borderRadius:6, padding:"4px 10px", fontSize:10, fontFamily:"monospace",
            cursor:"pointer" }}>{mode==="dark"?"☀":"☾"}</button>
          <button onClick={()=>{const nl=lang==="ko"?"en":"ko"; setLang(nl); POST("/api/auth/lang",{lang:nl}).catch(()=>{});}} style={{
            background:C.border2, color:C.text, border:`1px solid ${C.border2}`,
            borderRadius:6, padding:"4px 10px", fontSize:10, fontFamily:"monospace",
            cursor:"pointer", fontWeight:700 }}>{lang==="ko"?"EN":"KO"}</button>
          <button onClick={openProfile} style={{ background:C.border2, color:C.text, border:`1px solid ${C.border2}`,
            borderRadius:6, padding:"4px 10px", fontSize:10, fontFamily:"monospace",
            cursor:"pointer", fontWeight:600 }}>⚙ {me.name || me.id}</button>
          <button onClick={doLogout} style={{ background:C.red+"18", color:C.red,
            border:`1px solid ${C.red}44`, borderRadius:6, padding:"4px 12px",
            fontSize:10, fontFamily:"monospace", cursor:"pointer" }}>{t("logout")}</button>
        </div>
      </div>

      <div style={{ padding:24, maxWidth:1400, margin:"0 auto" }}>
        {tab==="overview" && isAdmin && <AdminOverview {...pageProps}/>}
        {tab==="users" && isAdmin && <AdminUsers {...pageProps}/>}
        {tab==="vms" && isAdmin && <AdminVMs {...pageProps}/>}
        {tab==="gpustatus" && <GpuStatus {...pageProps}/>}
        {tab==="metrics" && isAdmin && <AdminMetrics {...pageProps}/>}
        {tab==="ports" && isAdmin && <AdminPorts {...pageProps}/>}
        {tab==="requests" && isAdmin && <AdminRequests {...pageProps}/>}
        {tab==="myvms" && !isAdmin && <UserMyVMs {...pageProps}/>}
        {tab==="ssh" && !isAdmin && <UserSSH {...pageProps}/>}
        {tab==="services" && !isAdmin && <UserServices {...pageProps}/>}
        {tab==="requests" && !isAdmin && <UserRequests {...pageProps}/>}
        {tab==="guide" && !isAdmin && <UserGuide {...pageProps} setTab={setTab}/>}
      </div>

      {showProfile && (
        <div style={{ position:"fixed", inset:0, background:"#000b", zIndex:200,
          display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setShowProfile(false)}>
          <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:10,
            padding:24, minWidth:320, maxWidth:400 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:14, fontWeight:700, color:C.fg, marginBottom:16 }}>
              {t("profile") || "프로필"}
            </div>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:9, color:C.dim2, fontFamily:"monospace", letterSpacing:1, marginBottom:4, textTransform:"uppercase" }}>ID</div>
              <div style={{ fontSize:13, color:C.dim, padding:"7px 10px", background:C.bg2, borderRadius:4, border:`1px solid ${C.border}` }}>{me.id}</div>
            </div>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:9, color:C.dim2, fontFamily:"monospace", letterSpacing:1, marginBottom:4, textTransform:"uppercase" }}>{t("user_name") || "이름"}</div>
              <input value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})}
                style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border}`, color:C.fg, padding:"7px 10px", borderRadius:4, fontSize:13, boxSizing:"border-box" }} />
            </div>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:9, color:C.dim2, fontFamily:"monospace", letterSpacing:1, marginBottom:4, textTransform:"uppercase" }}>{t("current_password") || "현재 비밀번호"}</div>
              <input type="password" autoComplete="current-password" value={profileForm.currentPw}
                onChange={e => setProfileForm({...profileForm, currentPw: e.target.value})}
                style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border}`, color:C.fg, padding:"7px 10px", borderRadius:4, fontSize:13, boxSizing:"border-box" }} />
            </div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:9, color:C.dim2, fontFamily:"monospace", letterSpacing:1, marginBottom:4, textTransform:"uppercase" }}>{t("new_password") || "새 비밀번호"}</div>
              <input type="password" autoComplete="new-password" value={profileForm.newPw}
                onChange={e => setProfileForm({...profileForm, newPw: e.target.value})}
                style={{ width:"100%", background:C.bg2, border:`1px solid ${C.border}`, color:C.fg, padding:"7px 10px", borderRadius:4, fontSize:13, boxSizing:"border-box" }} />
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={() => setShowProfile(false)} style={{ background:C.bg2, color:C.fg, border:`1px solid ${C.border}`, borderRadius:6, padding:"6px 16px", fontSize:12, cursor:"pointer" }}>{t("cancel") || "취소"}</button>
              <button onClick={saveProfile} style={{ background:C.accent, color:"#fff", border:"none", borderRadius:6, padding:"6px 16px", fontSize:12, cursor:"pointer" }}>{t("save") || "저장"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
